import { useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from "recharts";
import { toast } from "sonner";
import {
  Plus,
  Download,
  Pencil,
  Search,
  SlidersHorizontal,
  Check,
  X,
  Info,
} from "lucide-react";

import { SelectorDeFecha } from "@/components/SelectorDeFecha";
import { PantallasConDiseno } from "@/components/design-system/PantallasConDiseno";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { KpiCard } from "@/components/KpiCard";
import { cn } from "@/lib/utils";

/* ===========================================================================
   Design System — página de referencia viva.
   Renderiza los componentes REALES de la app (no reimplementaciones), así
   sirve a la vez como catálogo y como QA visual del DS. Fuente de verdad:
   "Design System.dc.html" (claude.ai/design), adaptado al dominio Consultek.
=========================================================================== */

/* --- Átomos presentacionales locales (sólo se usan en esta página) --- */

function Section({
  n,
  title,
  id,
  children,
}: {
  n: string;
  title: string;
  id?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="mt-14 mb-6 flex items-center gap-3.5">
        <span className="text-xs font-extrabold text-muted-foreground/50">{n}</span>
        <h2 className="text-2xl font-extrabold tracking-tight">{title}</h2>
        <span className="h-px flex-1 bg-border" />
      </div>
      {children}
    </section>
  );
}

function Panel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-[1.25rem] border bg-card p-6 sm:p-7 ${className}`}>
      {children}
    </div>
  );
}

function Kicker({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-4 text-[0.6875rem] font-bold uppercase tracking-[0.08em] text-muted-foreground">
      {children}
    </div>
  );
}

/* El selector de fecha es controlado, así que en el catálogo necesita estado
   propio para que la muestra se pueda usar de verdad. */
function EjemploDeFecha() {
  const [fecha, setFecha] = useState("");

  return <SelectorDeFecha id="ds-fecha" value={fecha} onChange={setFecha} />;
}

/* Igual que el selector de fecha: el combobox es controlado, así que la muestra
   necesita estado propio para poder usarse de verdad en el catálogo.

   Las opciones son comunas REALES con su código del decreto, y comparten
   prefijos y acentos a propósito: es lo que deja probar acá mismo que "molina"
   y "mostazal" se separan por lo que EMPIEZA con lo escrito, que "nuble"
   encuentra Chillán Viejo por su región y que "073" filtra por código. */
const COMUNAS_DE_MUESTRA = [
  { valor: "07304", codigo: "07304", etiqueta: "Molina", pista: "Curicó · Maule" },
  { valor: "06110", codigo: "06110", etiqueta: "Mostazal", pista: "Cachapoal · O'Higgins" },
  { valor: "07301", codigo: "07301", etiqueta: "Curicó", pista: "Curicó · Maule" },
  { valor: "16103", codigo: "16103", etiqueta: "Chillán Viejo", pista: "Diguillín · Ñuble" },
  { valor: "14104", codigo: "14104", etiqueta: "Los Lagos", pista: "Valdivia · Los Ríos" },
];

function EjemploDeCombobox() {
  const [comuna, setComuna] = useState<string | null>(null);

  return (
    <Combobox
      id="ds-combobox"
      opciones={COMUNAS_DE_MUESTRA}
      valor={comuna}
      onValorChange={setComuna}
      placeholder="Selecciona una comuna"
      placeholderBusqueda="Buscar comuna o código…"
      mensajeVacio="Ninguna comuna coincide."
    />
  );
}

/* Toast con franja izquierda + ícono de color según tipo (patrón del DS).
   Se usa tanto en el catálogo estático como en los toasts reales (toast.custom). */
const toastVariants = {
  success: { strip: "border-l-[#3a9e37]", icon: "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300", Icon: Check },
  error: { strip: "border-l-[#e5544b]", icon: "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300", Icon: X },
  info: { strip: "border-l-[#4c82e5]", icon: "bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300", Icon: Info },
} as const;

function ToastCard({
  type,
  title,
  desc,
}: {
  type: keyof typeof toastVariants;
  title: string;
  desc: string;
}) {
  const v = toastVariants[type];
  return (
    <div
      className={cn(
        "flex w-full items-center gap-3 rounded-xl border border-l-4 border-border bg-card px-4 py-3.5 shadow-[0_8px_20px_-12px_rgba(20,22,26,0.25)]",
        v.strip
      )}
    >
      <span className={cn("flex size-7 shrink-0 items-center justify-center rounded-full", v.icon)}>
        <v.Icon className="size-4" strokeWidth={2.6} />
      </span>
      <div className="min-w-0">
        <div className="text-[0.84375rem] font-bold leading-tight">{title}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
    </div>
  );
}

/* --- Datos --- */

const greenScale = [
  ["50", "#eaf7e9"], ["100", "#d3f0d2"], ["200", "#abe1a9"], ["300", "#82d27f"],
  ["400", "#63c25f"], ["500", "#4cb648"], ["600", "#3a9e37"], ["700", "#2f7a2e"],
  ["800", "#245e23"], ["900", "#1b471a"],
];

const neutralScale = [
  ["0", "#ffffff"], ["50", "#f6f6f5"], ["100", "#ecedee"], ["200", "#e1e2e4"],
  ["300", "#c9cbce"], ["400", "#a6aab0"], ["500", "#7d8188"], ["600", "#5e6167"],
  ["800", "#2a2c30"], ["900", "#17181b"],
];

const semantic = [
  ["Success", "#3a9e37"], ["Warning", "#e9a23b"], ["Danger", "#e5544b"], ["Info", "#4c82e5"],
];

const surfaceTints = [
  ["Verde", "#ddf3de"], ["Ámbar", "#fce7d9"], ["Azul", "#e1e9f6"], ["Menta", "#d9ede6"],
];

const typeScale = [
  ["Display", "text-[2.5rem] font-extrabold tracking-[-0.03em]", "40 / 800"],
  ["Heading 1", "text-[1.875rem] font-extrabold tracking-[-0.02em]", "30 / 800"],
  ["Heading 2", "text-[1.375rem] font-extrabold tracking-[-0.01em]", "22 / 800"],
  ["Heading 3", "text-lg font-bold", "18 / 700"],
  ["Heading 4 — título de sección interna", "text-h4", "16 / 800"],
  ["Body — el veloz zorro marrón salta", "text-[0.9375rem] font-medium text-foreground/80", "15 / 500"],
  ["Caption / texto de ayuda", "text-xs font-semibold text-muted-foreground", "12 / 600"],
];

const radii = [
  ["sm · 8px", "rounded-sm"], ["md · 12px", "rounded-md"],
  ["lg · 14px", "rounded-lg"], ["xl · 20px", "rounded-xl"],
];

const kpis = [
  { label: "Cotizaciones", value: "128", delta: "+17%", tint: "bg-primary/10", color: "#4cb648", spark: "M0,36 C30,34 45,22 75,26 C105,30 120,10 150,12 C175,14 188,8 200,6" },
  { label: "Expedientes", value: "42", delta: "+5%", tint: "bg-chart-2/10", color: "#6d8fd6", spark: "M0,30 C28,32 44,20 72,24 C100,26 120,14 150,16 C175,18 188,22 200,14" },
  { label: "Hectáreas tasadas", value: "9.6k", delta: "+44%", tint: "bg-chart-3/10", color: "#e08a5a", spark: "M0,12 C28,14 44,28 72,28 C100,28 120,18 148,24 C172,29 188,32 200,30" },
];

const barData = [
  { d: "Lun", v: 40 }, { d: "Mar", v: 90 }, { d: "Mié", v: 45 }, { d: "Jue", v: 110 },
  { d: "Vie", v: 72 }, { d: "Sáb", v: 95 }, { d: "Dom", v: 52 },
];

const donutData = [
  { name: "Agrícola", value: 45, color: "#4cb648" },
  { name: "Forestal", value: 30, color: "#6d8fd6" },
  { name: "Agroindustrial", value: 15, color: "#e08a5a" },
  { name: "Otro", value: 10, color: "#e1e2e4" },
];

const lineData = [
  { m: "Ene", tasado: 40, ingresado: 30 }, { m: "Feb", tasado: 58, ingresado: 42 },
  { m: "Mar", tasado: 50, ingresado: 48 }, { m: "Abr", tasado: 72, ingresado: 55 },
  { m: "May", tasado: 66, ingresado: 60 }, { m: "Jun", tasado: 88, ingresado: 62 },
  { m: "Jul", tasado: 79, ingresado: 70 },
];

const tableRows = [
  { ini: "VS", name: "Viñedos del Sur", grad: "from-[#cfe6d3] to-[#93cf9c]", fg: "#2f7a2e", date: "08 ene, 2026", tipo: "Agrícola", status: "Completada", variant: "success" as const, amount: "$1.200 M" },
  { ini: "HS", name: "Hacienda Santa María", grad: "from-[#cdd9f2] to-[#8fa6da]", fg: "#3f5aa0", date: "08 ene, 2026", tipo: "Forestal", status: "Completada", variant: "success" as const, amount: "$1.750 M" },
  { ini: "BC", name: "Banco Central", grad: "from-[#f6d9c8] to-[#e6a985]", fg: "#a05a34", date: "07 ene, 2026", tipo: "Peritaje", status: "Pendiente", variant: "warning" as const, amount: "$500 M" },
  { ini: "RF", name: "Agrícola Robles", grad: "from-[#e3d3f2] to-[#b48fda]", fg: "#6a3fa0", date: "06 ene, 2026", tipo: "Agroindustrial", status: "Rechazada", variant: "destructive" as const, amount: "$4.300 M" },
];

export function DesignSystem() {
  const [tab, setTab] = useState("overview");

  return (
    <TooltipProvider delayDuration={100}>
      <div className="mx-auto max-w-[73.75rem] p-6 pb-24 lg:p-8">
        {/* Intro */}
        <div className="max-w-2xl">
          <div className="mb-3 text-[0.6875rem] font-bold uppercase tracking-[0.14em] text-primary">
            Consultek SaaS · UI Kit v1.0
          </div>
          <h1 className="text-4xl font-extrabold leading-[1.05] tracking-[-0.03em]">
            Design System
          </h1>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Catálogo vivo de componentes, tokens y patrones. Todo lo que ves aquí
            son los componentes reales de la aplicación: esta página funciona como
            referencia y como control de calidad visual del sistema.
          </p>
        </div>

        {/* ============ 01 FOUNDATIONS ============ */}
        <Section n="01" title="Foundations" id="foundations">
          <Panel className="mb-5">
            <Kicker>Color — Primario (Consultek Green)</Kicker>
            <div className="overflow-hidden rounded-xl">
              <div className="grid grid-cols-5 sm:grid-cols-10">
                {greenScale.map(([, hex]) => (
                  <div key={hex} className="h-16" style={{ background: hex }} />
                ))}
              </div>
            </div>
            <div className="mt-2 grid grid-cols-5 gap-y-2 sm:grid-cols-10">
              {greenScale.map(([n, hex]) => (
                <div key={hex} className="text-[0.65625rem]">
                  <div className="font-bold">{n}</div>
                  <div className="font-mono text-muted-foreground">{hex.slice(1).toUpperCase()}</div>
                </div>
              ))}
            </div>

            <div className="mt-7">
              <Kicker>Color — Neutral</Kicker>
              <div className="overflow-hidden rounded-xl border">
                <div className="grid grid-cols-5 sm:grid-cols-10">
                  {neutralScale.map(([, hex]) => (
                    <div key={hex} className="h-16" style={{ background: hex }} />
                  ))}
                </div>
              </div>
              <div className="mt-2 grid grid-cols-5 gap-y-2 sm:grid-cols-10">
                {neutralScale.map(([n, hex]) => (
                  <div key={hex} className="text-[0.65625rem]">
                    <div className="font-bold">{n}</div>
                    <div className="font-mono text-muted-foreground">{hex.slice(1).toUpperCase()}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-7 grid gap-8 sm:grid-cols-2">
              <div>
                <Kicker>Semántico</Kicker>
                <div className="grid grid-cols-4 gap-3">
                  {semantic.map(([name, hex]) => (
                    <div key={name}>
                      <div className="h-14 rounded-xl" style={{ background: hex }} />
                      <div className="mt-1.5 text-xs font-bold">{name}</div>
                      <div className="font-mono text-[0.625rem] text-muted-foreground">{hex.slice(1).toUpperCase()}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Kicker>Tintes de superficie</Kicker>
                <div className="grid grid-cols-4 gap-3">
                  {surfaceTints.map(([name, hex]) => (
                    <div key={name}>
                      <div className="h-14 rounded-xl" style={{ background: hex }} />
                      <div className="mt-1.5 text-xs font-bold">{name}</div>
                      <div className="font-mono text-[0.625rem] text-muted-foreground">{hex.slice(1).toUpperCase()}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Panel>

          <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
            <Panel>
              <Kicker>Tipografía — Plus Jakarta Sans</Kicker>
              <div className="flex flex-col divide-y divide-border">
                {typeScale.map(([label, cls, spec]) => (
                  <div key={spec} className="flex items-baseline justify-between gap-4 py-4 first:pt-0 last:pb-0">
                    <span className={cls as string}>{label}</span>
                    <span className="shrink-0 font-mono text-[0.6875rem] text-muted-foreground">{spec}</span>
                  </div>
                ))}
              </div>
            </Panel>
            <Panel>
              <Kicker>Radio y elevación</Kicker>
              <div className="grid grid-cols-2 gap-3.5">
                {radii.map(([label, cls]) => (
                  <div key={label} className="text-center">
                    <div className={`h-14 border-[1.5px] border-primary/40 bg-accent ${cls}`} />
                    <div className="mt-2 text-[0.6875rem] font-bold">{label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="h-14 rounded-xl bg-card shadow-sm" />
                  <div className="mt-2.5 text-[0.6875rem] font-bold">shadow-sm</div>
                </div>
                <div className="text-center">
                  <div className="h-14 rounded-xl bg-card shadow-[0_18px_40px_-18px_rgba(20,22,26,.4)]" />
                  <div className="mt-2.5 text-[0.6875rem] font-bold">shadow-lg</div>
                </div>
              </div>
            </Panel>
          </div>
        </Section>

        {/* ============ 02 BUTTONS ============ */}
        <Section n="02" title="Buttons" id="buttons">
          <Panel>
            <Kicker>Variantes</Kicker>
            <div className="mb-7 flex flex-wrap items-center gap-3.5">
              <Button>Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="soft">Soft</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="destructive">Danger</Button>
              <Button variant="dashed">Dashed</Button>
              <Button variant="fuerte">Fuerte</Button>
            </div>

            <p className="text-ink-hint mb-7 max-w-prose text-xs">
              <b className="text-ink-body">Dashed</b> es la affordance de “agregar”:
              superficie de card sobre el canvas + borde punteado. Úsala solo para
              sumar un ítem a una lista o pipeline (“Agregar prompt”), nunca como
              botón de acción principal.
            </p>

            <div className="grid gap-8 sm:grid-cols-2">
              <div>
                <Kicker>Tamaños</Kicker>
                <div className="flex flex-wrap items-center gap-3">
                  <Button size="sm">Small</Button>
                  <Button>Medium</Button>
                  <Button size="lg">Large</Button>
                </div>
              </div>
              <div>
                <Kicker>Estados</Kicker>
                <div className="flex flex-wrap items-center gap-3">
                  <Button>Default</Button>
                  <Button className="bg-primary/90 shadow-[0_0_0_4px_rgba(76,182,72,0.22),0_8px_18px_-8px_rgba(76,182,72,0.65)]">Hover</Button>
                  <Button disabled>Disabled</Button>
                </div>
              </div>
            </div>

            <div className="mt-7">
              <Kicker>Con ícono e icon-only</Kicker>
              <div className="flex flex-wrap items-center gap-3">
                <Button><Plus />Nuevo</Button>
                <Button variant="outline">Descargar<Download /></Button>
                <Button variant="soft" size="icon"><Pencil /></Button>
                <Button size="icon" className="rounded-full"><Plus /></Button>
              </div>
            </div>
          </Panel>
        </Section>

        {/* ============ 03 FORMS ============ */}
        <Section n="03" title="Forms & Inputs" id="forms">
          <Panel>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {/* Columna 1: text inputs */}
              <div className="flex flex-col gap-5">
                <div className="grid gap-2">
                  <Label htmlFor="ds-name">Nombre completo</Label>
                  <Input id="ds-name" defaultValue="Alex Moreno" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ds-email">
                    Email <span className="font-semibold text-primary">(foco)</span>
                  </Label>
                  <Input
                    id="ds-email"
                    defaultValue="alex@consultek.cl"
                    className="border-ring ring-[3px] ring-ring/40"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ds-pass">Contraseña</Label>
                  <Input id="ds-pass" type="password" defaultValue="12345678" aria-invalid />
                  <p className="text-xs font-semibold text-destructive">
                    Debe tener al menos 8 caracteres.
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ds-fecha">Fecha</Label>
                  <EjemploDeFecha />
                  <p className="text-xs text-ink-hint">
                    Reemplaza a <code>{'<input type="date">'}</code>, que dibuja mm/dd/yyyy
                    según el idioma del navegador. Se teclea con máscara —los guiones los
                    pone solo, y acepta que escribas las barras— y el calendario queda como
                    atajo para la fecha que se piensa en vez de saberse.
                  </p>
                </div>
              </div>

              {/* Columna 2: search, select, textarea */}
              <div className="flex flex-col gap-5">
                <div className="grid gap-2">
                  <Label>Búsqueda</Label>
                  <div className="flex h-11 items-center gap-2.5 rounded-md border-[1.5px] border-input px-3.5">
                    <Search className="h-[1.0625rem] w-[1.0625rem] text-muted-foreground" />
                    <input
                      placeholder="Buscar expedientes…"
                      className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label>Tipo de tasación</Label>
                  <Select defaultValue="agricola">
                    <SelectTrigger className="h-11 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agricola">Agrícola</SelectItem>
                      <SelectItem value="forestal">Forestal</SelectItem>
                      <SelectItem value="agroindustrial">Agroindustrial</SelectItem>
                      <SelectItem value="peritaje">Peritaje judicial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ds-combobox">Comuna (combobox)</Label>
                  <EjemploDeCombobox />
                  <p className="text-xs text-ink-hint">
                    Select CON buscador, para maestros que no caben en una lista: hasta
                    ~15 opciones va el <code>NativeSelect</code>. Ignora acentos al
                    buscar (&quot;nuble&quot; encuentra Ñuble), la línea secundaria
                    desambigua los homónimos y el código de la derecha también se
                    busca (&quot;073&quot;).
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ds-note">Nota</Label>
                  <Textarea id="ds-note" placeholder="Agregar una nota…" />
                </div>
              </div>

              {/* Columna 3: choices */}
              <div className="flex flex-col gap-6">
                <div>
                  <div className="mb-3 text-xs font-bold text-foreground/80">Checkbox</div>
                  <div className="flex flex-col gap-3">
                    <Label className="text-sm font-medium">
                      <Checkbox defaultChecked /> Notificaciones por email
                    </Label>
                    <Label className="text-sm font-medium text-muted-foreground">
                      <Checkbox /> Alertas por SMS
                    </Label>
                  </div>
                </div>
                <div>
                  <div className="mb-3 text-xs font-bold text-foreground/80">Radio</div>
                  <RadioGroup defaultValue="mensual" className="gap-3">
                    <Label className="text-sm font-medium">
                      <RadioGroupItem value="mensual" /> Plan mensual
                    </Label>
                    <Label className="text-sm font-medium text-muted-foreground">
                      <RadioGroupItem value="anual" /> Plan anual
                    </Label>
                  </RadioGroup>
                </div>
                <div>
                  <div className="mb-3 text-xs font-bold text-foreground/80">Toggle</div>
                  <div className="flex items-center gap-4">
                    <Switch defaultChecked />
                    <Switch />
                  </div>
                </div>
              </div>
            </div>
          </Panel>
        </Section>

        {/* ============ 04 DATA ============ */}
        <Section n="04" title="Cards, KPIs y Data" id="data">
          {/* KPIs + tarjeta oscura */}
          <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {kpis.map((k) => (
              <KpiCard key={k.label} {...k} />
            ))}
            <div className="rounded-[1.25rem] bg-[#17181b] p-5 text-white dark:ring-1 dark:ring-white/10">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">Valor cartera</span>
                <span className="text-xs font-semibold text-[#4cd06a]">↑ 10%</span>
              </div>
              <div className="mt-5 text-[1.75rem] font-extrabold tracking-[-0.02em]">$4.850 M</div>
              <div className="mt-4 flex gap-2">
                <button className="flex-1 rounded-[0.625rem] bg-white py-2.5 text-xs font-bold text-[#17181b]">Ingresar</button>
                <button className="flex-1 rounded-[0.625rem] border-[1.5px] border-white/25 py-2.5 text-xs font-bold text-white">Retirar</button>
              </div>
            </div>
          </div>

          {/* Tabla real */}
          <div className="mb-5 overflow-hidden rounded-[1.25rem] border bg-card">
            <div className="flex items-center justify-between px-6 py-5">
              <h3 className="text-[1.0625rem] font-extrabold">Tasaciones recientes</h3>
              <Button variant="outline" size="sm"><SlidersHorizontal />Filtrar</Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {/* `variant="versalitas"` y no la cadena de clases a mano:
                      el catálogo tiene que mostrar el componente REAL, o deja
                      de servir como QA visual — esta copia difería del
                      encabezado que usan las pantallas en el tamaño y en el
                      color. */}
                  <TableHead variant="versalitas" className="px-6">Cliente</TableHead>
                  <TableHead variant="versalitas">Fecha</TableHead>
                  <TableHead variant="versalitas">Tipo</TableHead>
                  <TableHead variant="versalitas">Estado</TableHead>
                  <TableHead variant="versalitas" className="px-6 text-right">Monto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableRows.map((r) => (
                  <TableRow key={r.name}>
                    <TableCell className="px-6 py-3.5">
                      <div className="flex items-center gap-3">
                        <span className={`flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br ${r.grad} text-xs font-bold`} style={{ color: r.fg }}>
                          {r.ini}
                        </span>
                        <span className="font-bold">{r.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground font-medium">{r.date}</TableCell>
                    <TableCell className="text-muted-foreground font-medium">{r.tipo}</TableCell>
                    <TableCell>
                      <Badge variant={r.variant}>
                        <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-current" />
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-6 text-right font-extrabold">{r.amount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Gráficos */}
          <div className="grid gap-5 lg:grid-cols-3">
            <Panel className="lg:col-span-2">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-base font-extrabold">Gráfico de barras</h3>
                <span className="text-xs font-semibold text-muted-foreground">Semanal</span>
              </div>
              <div className="h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="rgba(125,129,136,0.15)" />
                    <XAxis dataKey="d" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#a6aab0" }} />
                    <RechartsTooltip
                      cursor={{ fill: "rgba(125,129,136,0.08)" }}
                      contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 12, fontSize: 13 }}
                    />
                    <Bar dataKey="v" radius={[7, 7, 0, 0]} isAnimationActive={false}>
                      {barData.map((e) => (
                        <Cell key={e.d} fill={e.v === 110 ? "#4cb648" : "#d3f0d2"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
            <Panel>
              <h3 className="mb-2 text-base font-extrabold">Gráfico de dona</h3>
              <div className="flex items-center gap-5">
                <div className="relative h-[8.75rem] w-[8.75rem] shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={donutData} dataKey="value" innerRadius={48} outerRadius={68} paddingAngle={2} stroke="none" isAnimationActive={false}>
                        {donutData.map((e) => (
                          <Cell key={e.name} fill={e.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-lg font-extrabold">72%</span>
                    <span className="text-[0.625rem] text-muted-foreground">cerradas</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2.5 text-xs font-semibold">
                  {donutData.map((e) => (
                    <span key={e.name} className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: e.color }} />
                      {e.name} {e.value}%
                    </span>
                  ))}
                </div>
              </div>
            </Panel>
          </div>

          <Panel className="mt-5">
            <div className="mb-4 flex flex-wrap items-center gap-4">
              <h3 className="text-base font-extrabold">Gráfico de línea</h3>
              <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                <span className="h-2 w-2 rounded-full bg-foreground" />Valor tasado
              </span>
              <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                <span className="h-2 w-2 rounded-full bg-chart-3" />Ingresado
              </span>
            </div>
            <div className="h-40 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lineData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="rgba(125,129,136,0.15)" />
                  <XAxis dataKey="m" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#a6aab0" }} />
                  <RechartsTooltip contentStyle={{ background: "var(--popover)", border: "1px solid var(--border)", borderRadius: 12, fontSize: 13 }} />
                  <Line type="monotone" dataKey="tasado" stroke="var(--foreground)" strokeWidth={2.6} dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="ingresado" stroke="#e08a5a" strokeWidth={2.6} strokeDasharray="3 6" dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </Section>

        {/* ============ 05 NAVIGATION ============ */}
        <Section n="05" title="Navigation" id="nav">
          <div className="grid gap-5 lg:grid-cols-[1fr_1.4fr]">
            <Panel>
              <Kicker>Ítems de sidebar</Kicker>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-3 rounded-xl bg-card px-3.5 py-3 text-[0.9375rem] font-bold shadow-[0_4px_14px_-8px_rgba(20,22,26,.2)]">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />Resumen
                </div>
                <div className="flex items-center gap-3 rounded-xl px-3.5 py-3 text-[0.9375rem] font-medium text-muted-foreground">
                  <span className="h-1.5 w-1.5" />Cotizaciones
                </div>
                <div className="flex items-center gap-3 rounded-xl px-3.5 py-3 text-[0.9375rem] font-medium text-muted-foreground">
                  <span className="h-1.5 w-1.5" />Expedientes
                </div>
              </div>
            </Panel>
            <Panel>
              <Kicker>Tabs</Kicker>
              <Tabs value={tab} onValueChange={setTab} variant="underline" className="mb-6">
                <TabsList>
                  <TabsTrigger value="overview">Resumen</TabsTrigger>
                  <TabsTrigger value="analytics">Analítica</TabsTrigger>
                  <TabsTrigger value="reports">Informes</TabsTrigger>
                  <TabsTrigger value="settings">Ajustes</TabsTrigger>
                </TabsList>
              </Tabs>

              <Kicker>Breadcrumbs</Kicker>
              <Breadcrumb className="mb-6">
                <BreadcrumbList>
                  <BreadcrumbItem><BreadcrumbLink href="#">Dashboard</BreadcrumbLink></BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem><BreadcrumbLink href="#">Expedientes</BreadcrumbLink></BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem><BreadcrumbPage>EXP-2026-004</BreadcrumbPage></BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>

              <Kicker>Paginación</Kicker>
              <Pagination className="justify-start">
                <PaginationContent>
                  <PaginationItem><PaginationPrevious href="#" /></PaginationItem>
                  <PaginationItem><PaginationLink href="#" isActive>1</PaginationLink></PaginationItem>
                  <PaginationItem><PaginationLink href="#">2</PaginationLink></PaginationItem>
                  <PaginationItem><PaginationLink href="#">3</PaginationLink></PaginationItem>
                  <PaginationItem><PaginationEllipsis /></PaginationItem>
                  <PaginationItem><PaginationNext href="#" /></PaginationItem>
                </PaginationContent>
              </Pagination>
            </Panel>
          </div>

          {/* Badges / chips / avatars */}
          <Panel className="mt-5">
            <div className="grid gap-8 md:grid-cols-3">
              <div>
                <Kicker>Badges</Kicker>
                <div className="mb-3.5 flex flex-wrap gap-2.5">
                  <Badge variant="success">Activo</Badge>
                  <Badge variant="warning">Pendiente</Badge>
                  <Badge variant="danger">Fallido</Badge>
                  <Badge variant="destructive">Rechazado</Badge>
                  <Badge variant="info">Info</Badge>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  <Badge>Sólido</Badge>
                  <Badge variant="secondary">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />Con punto
                  </Badge>
                </div>
              </div>
              <div>
                <Kicker>Chips</Kicker>
                <div className="flex flex-wrap gap-2.5">
                  <Badge className="rounded-full bg-foreground px-3.5 py-1.5 text-[0.8125rem] font-semibold text-background">Todos</Badge>
                  <Badge variant="secondary" className="px-3.5 py-1.5 text-[0.8125rem] font-semibold">Agrícola</Badge>
                  <Badge variant="secondary" className="px-3.5 py-1.5 text-[0.8125rem] font-semibold">Forestal</Badge>
                  <Badge variant="success" className="px-3.5 py-1.5 text-[0.8125rem] font-semibold">
                    Ahorro<X className="h-3 w-3" />
                  </Badge>
                </div>
              </div>
              <div>
                <Kicker>Avatares</Kicker>
                <div className="mb-4 flex items-center gap-3">
                  <Avatar className="size-8"><AvatarFallback className="bg-gradient-to-br from-[#cfe6d3] to-[#93cf9c] text-xs text-[#2f7a2e]">AM</AvatarFallback></Avatar>
                  <Avatar className="size-10"><AvatarFallback className="bg-gradient-to-br from-[#cfe6d3] to-[#93cf9c] text-sm text-[#2f7a2e]">AM</AvatarFallback></Avatar>
                  <div className="relative">
                    <Avatar className="size-12"><AvatarFallback className="bg-gradient-to-br from-[#cfe6d3] to-[#93cf9c] text-base text-[#2f7a2e]">AM</AvatarFallback></Avatar>
                    <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-[2.5px] border-card bg-primary" />
                  </div>
                </div>
                <div className="flex items-center">
                  {[
                    ["JM", "from-[#cfe6d3] to-[#93cf9c]", "#2f7a2e"],
                    ["SK", "from-[#f6d9c8] to-[#e6a985]", "#a05a34"],
                    ["RD", "from-[#cdd9f2] to-[#8fa6da]", "#3f5aa0"],
                  ].map(([ini, grad, fg], i) => (
                    <Avatar key={ini} className={`size-9 border-[2.5px] border-card ${i > 0 ? "-ml-3" : ""}`}>
                      <AvatarFallback className={`bg-gradient-to-br ${grad} text-[0.8125rem]`} style={{ color: fg }}>{ini}</AvatarFallback>
                    </Avatar>
                  ))}
                  <Avatar className="-ml-3 size-9 border-[2.5px] border-card">
                    <AvatarFallback className="bg-muted text-xs text-muted-foreground">+5</AvatarFallback>
                  </Avatar>
                </div>
              </div>
            </div>
          </Panel>
        </Section>

        {/* ============ 06 FEEDBACK ============ */}
        <Section n="06" title="Overlays & Feedback" id="feedback">
          <div className="grid gap-5 lg:grid-cols-2">
            <Panel>
              <Kicker>Modal (Dialog)</Kicker>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline">Abrir modal</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                      <Check className="h-5 w-5" />
                    </div>
                    <DialogTitle>Confirmar envío</DialogTitle>
                    <DialogDescription>
                      Estás por enviar la cotización <strong className="text-foreground">COT-2026-018</strong> al
                      cliente. Esta acción no se puede deshacer.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <DialogClose asChild><Button variant="outline">Cancelar</Button></DialogClose>
                    <DialogClose asChild><Button>Confirmar</Button></DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </Panel>

            <Panel>
              <Kicker>Toasts</Kicker>
              <div className="mb-5 flex flex-col gap-3">
                <ToastCard type="success" title="Cotización enviada" desc="COT-2026-018 al cliente" />
                <ToastCard type="error" title="Envío fallido" desc="Sin conexión con el servidor" />
                <ToastCard type="info" title="Informe listo" desc="Enero 2026 disponible" />
              </div>
              <div className="mb-6 flex flex-wrap gap-3">
                <Button variant="soft" size="sm" onClick={() => toast.custom(() => <ToastCard type="success" title="Cotización enviada" desc="COT-2026-018 al cliente" />)}>
                  <Check />Probar éxito
                </Button>
                <Button variant="outline" size="sm" onClick={() => toast.custom(() => <ToastCard type="error" title="Envío fallido" desc="Sin conexión con el servidor" />)}>
                  <X />Error
                </Button>
                <Button variant="outline" size="sm" onClick={() => toast.custom(() => <ToastCard type="info" title="Informe listo" desc="Enero 2026 disponible" />)}>
                  <Info />Info
                </Button>
              </div>

              <Kicker>Tooltip</Kicker>
              <div className="flex justify-center py-3">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="soft">Pasar el cursor</Button>
                  </TooltipTrigger>
                  <TooltipContent>El valor creció 10% este mes</TooltipContent>
                </Tooltip>
              </div>
            </Panel>
          </div>
        </Section>

        {/* ============ 07 PANTALLAS CON DISEÑO ============ */}
        <Section n="07" title="Pantallas con diseño" id="pantallas">
          <PantallasConDiseno />
        </Section>

        <div className="mt-14 text-center text-sm text-muted-foreground">
          Consultek Design System · construido para SaaS
        </div>
      </div>
    </TooltipProvider>
  );
}
